// react
import { useState } from 'react';

// third-party
import * as yup from 'yup';
import { Form, FormikProvider, useFormik } from 'formik';
import { Button, CardContent, DialogTitle, Divider, InputLabel, Stack, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';

// project import
import RequiredAsterisk from 'components/RequiredAsterisk';
import { dispatch, useSelector } from 'store';
import { closeCreateForm } from 'store/reducers/team';
import TeamDto, { AgeType, FootballType, Gender, TeamType } from 'common/dto-types/team-dto';

type TeamFormFields = {
    id: number;
    name: string;
    footballType: FootballType;
    teamType: TeamType;
    ageType: AgeType;
    gender: Gender;
    abbreviation: string;
    pictureUrl: string;
};

type TeamFormProps = {
    onSubmit?: (team: TeamDto) => Promise<void>;
};

const TeamForm = (props: TeamFormProps): JSX.Element => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const data = useSelector((state) => state.team.data);
    const isCreating = !data;

    const convertToTeamDto = (team: TeamFormFields): TeamDto => {
        const dto: TeamDto = {
            id: team.id,
            name: team.name,
            footballType: team.footballType,
            teamType: team.teamType,
            ageType: team.ageType,
            gender: team.gender,
            abbreviation: team.abbreviation,
            pictureUrl: team.pictureUrl,
        };

        return dto;
    };

    const initialValues: TeamFormFields = {
        id: 0,
        name: '',
        footballType: FootballType.Unknown,
        teamType: TeamType.Club,
        ageType: AgeType.Unknown,
        gender: Gender.Unknown,
        abbreviation: '',
        pictureUrl: '',
    };

    const validationSchema = yup.object({
        name: yup.string().required('Name is required').max(255, 'Name cannot exceed 255 characters'),
    });

    const formik = useFormik<TeamFormFields>({
        initialValues,
        validationSchema,
        onSubmit: async (values) => {
            if (!props.onSubmit) return;

            const dto = convertToTeamDto(values);

            setIsLoading(true);
            await props.onSubmit(dto);
            setIsLoading(false);
        },
    });

    const { errors, touched, dirty, isValid, getFieldProps, handleSubmit } = formik;

    return (
        <FormikProvider value={formik}>
            <Form onSubmit={handleSubmit}>
                <DialogTitle>{isCreating ? 'Add' : 'Update'} Team</DialogTitle>
                <CardContent>
                    <Stack spacing={3}>
                        <Stack spacing={1}>
                            <InputLabel>
                                Team Name <RequiredAsterisk />
                            </InputLabel>
                            <TextField
                                fullWidth
                                id="name"
                                placeholder="Enter Team Name"
                                error={touched.name && Boolean(errors.name)}
                                helperText={touched.name && errors.name}
                                {...getFieldProps('name')}
                            />
                        </Stack>
                    </Stack>
                </CardContent>
                <Divider />
                <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ px: 2.5, py: 2 }}>
                    <Button color="error" size="small" disabled={isLoading} onClick={() => dispatch(closeCreateForm())}>
                        Cancel
                    </Button>
                    <LoadingButton
                        type="submit"
                        variant="contained"
                        size="small"
                        loading={isLoading}
                        disabled={!isValid || (isCreating && !dirty)}
                    >
                        Save
                    </LoadingButton>
                </Stack>
            </Form>
        </FormikProvider>
    );
};

export default TeamForm;
